import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, CheckCircle2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { useLang, nativeGuides } from "@/context/LangContext";
import { http, formatErr, useApi } from "@/lib/api";
import { CarAddons, TransportField, addonCost } from "@/components/site/BookingExtras";
import { LevyNote } from "@/components/site/TourCard";
import { useSiteUi } from "@/context/SiteUiContext";

const tomorrow = () => new Date(Date.now() + 86400000).toISOString().slice(0, 10);

export default function BookingDialog({ item, onClose }) {
  const { user } = useAuth();
  const { t, lang, money, currency, ratesReady, ratesError, retryRates } = useLang();
  const { mobile, editing } = useSiteUi();
  const submitting = useRef(false);
  const errorRef = useRef(null);
  const scrollRef = useRef(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ date: tomorrow(), quantity: 1, contact_name: user?.name || "", phone: user?.phone || "", notes: "", transport_ref: "" });
  const [addons, setAddons] = useState([]);
  const allGuides = useApi(item?.type === "tour" ? "/guides" : null).data;
  const guides = nativeGuides(allGuides, lang);
  const [guideSlug, setGuideSlug] = useState("");
  const guide = guides.find((g) => g.slug === guideSlug);
  const guideCost = guide ? guide.price * (item?.days || 1) : 0;
  const needsRef = item?.type === "car" || item?.type === "tour";
  const extras = item?.type === "car" ? addonCost(addons, Number(form.quantity) || 0) : 0;
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  useEffect(() => { if (error) errorRef.current?.scrollIntoView({ block: "nearest" }); }, [error]);
  useEffect(() => { if (done && scrollRef.current) scrollRef.current.scrollTop = 0; }, [done]);
  if (!item) return null;
  const qtyLabel = item.type === "tour" ? t("participants") : t("rent_days");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (submitting.current || !ratesReady) return;
    setError("");
    if (form.phone.replace(/\D/g, "").length < 8) { setError(t("pt_phone_invalid")); return; }
    submitting.current = true;
    setBusy(true);
    try {
      const { data } = await http.post("/bookings", { ...form, transport_ref: needsRef ? form.transport_ref : null, addons: item.type === "car" ? addons : [], currency, guide_slug: guide ? guide.slug : null, quantity: Number(form.quantity), item_type: item.type, item_slug: item.slug, item_name: item.name, lang });
      setDone(data);
      window.open(data.whatsapp_url, "_blank");
    } catch (err) { setError(formatErr(err)); } finally { submitting.current = false; setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="booking-dialog" className="booking-dialog sm:max-w-md rounded-3xl" closeLabel={t("mobile_close")} aria-describedby={undefined} onOpenAutoFocus={(e) => { if (mobile) e.preventDefault(); }}>
        <DialogHeader className="booking-dialog-header"><DialogTitle className="pr-12 text-base leading-snug sm:text-xl [overflow-wrap:anywhere]">{t("booking_title")} · {item.name}</DialogTitle></DialogHeader>
        <div ref={scrollRef} data-testid="booking-dialog-scroll" className="booking-dialog-scroll" data-editing={editing}>
        {done ? (
          <div className="space-y-4 text-center py-4" data-testid="booking-success">
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-600" />
            <p className="font-semibold">{t("booking_ok")}</p>
            <p className="text-sm text-slate-500">{t("booking_code")}: <b data-testid="booking-code" className="text-slate-900">{done.code}</b></p>
            {done.guest && <p data-testid="booking-guest-hint" className="rounded-2xl bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-900">{t("booking_guest_hint")}</p>}
            <Link to="/booking-status" state={{ code: done.code }} onClick={onClose} data-testid="booking-check-status" className="block rounded-full border border-emerald-200 py-3 text-sm font-bold text-[#064E3B] transition-colors hover:bg-emerald-50">{t("check_booking")}</Link>
            <p data-testid="booking-code-secret" className="text-xs leading-relaxed text-slate-500">{t("access_secret")}</p>
            <a href={done.whatsapp_url} target="_blank" rel="noreferrer" data-testid="booking-whatsapp-button" className="flex items-center justify-center gap-2 rounded-full bg-[#25D366] py-3 font-bold text-white hover:brightness-95"><MessageCircle className="h-5 w-5" />{t("open_wa")}</a>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="booking-form" className="space-y-4">
            <div className="booking-date-quantity grid grid-cols-2 gap-3">
              <div><Label htmlFor="booking-date">{t("date")}</Label><Input id="booking-date" data-testid="booking-date-input" type="date" min={tomorrow()} value={form.date} onChange={set("date")} required /></div>
              <div><Label htmlFor="booking-quantity">{qtyLabel}</Label><Input id="booking-quantity" data-testid="booking-qty-input" type="number" inputMode="numeric" min={1} max={100} value={form.quantity} onChange={set("quantity")} required /></div>
            </div>
            <div><Label htmlFor="booking-name">{t("full_name")}</Label><Input id="booking-name" autoComplete="name" data-testid="booking-name-input" value={form.contact_name} onChange={set("contact_name")} required /></div>
            <div><Label htmlFor="booking-phone">{t("phone")}</Label><Input id="booking-phone" type="tel" inputMode="tel" autoComplete="tel" data-testid="booking-phone-input" placeholder="+62… / +31…" value={form.phone} onChange={set("phone")} required /></div>
            {needsRef && <TransportField value={form.transport_ref} onChange={(v) => setForm({ ...form, transport_ref: v })} />}
            {item.type === "car" && <CarAddons value={addons} onChange={setAddons} />}
            <div><Label htmlFor="booking-notes">{t("notes")}</Label><Textarea id="booking-notes" data-testid="booking-notes-input" rows={2} value={form.notes} onChange={set("notes")} /></div>
            {item.type === "tour" && guides.length > 0 && (
              <div data-testid="booking-guide-addon" className="rounded-2xl border border-amber-200 bg-amber-50 p-3">
                <label className="flex min-h-11 items-center gap-2 text-sm font-semibold text-slate-800">
                  <input type="checkbox" data-testid="booking-guide-checkbox" checked={!!guide} onChange={(e) => setGuideSlug(e.target.checked ? guides[0].slug : "")} className="h-4 w-4 accent-[#064E3B]" />
                  {t("addon_guide")}
                </label>
                {guide && (
                  <select data-testid="booking-guide-select" value={guideSlug} onChange={(e) => setGuideSlug(e.target.value)} className="mt-2 w-full rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm outline-none">
                    {guides.map((g) => <option key={g.slug} value={g.slug}>{g.name} · {g.languages.join(", ")} · {money(g.price)}{t("per_day")}</option>)}
                  </select>
                )}
              </div>
            )}
            {error && <p ref={errorRef} role="alert" data-testid="booking-form-error" className="rounded-xl bg-rose-50 p-3 text-sm leading-relaxed text-rose-700">{error}</p>}
            <div className="booking-actions space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-emerald-50 px-4 py-3">
              <span className="text-sm text-slate-600">{t("estimate")}</span>
              <b data-testid="booking-estimate" className="text-lg text-[#064E3B]">{money(item.price * (Number(form.quantity) || 0) + guideCost + extras, item.city, item.type === "tour" ? Number(form.quantity) || 0 : 0)}</b>
            </div>
            {!ratesReady && ratesError && <button type="button" data-testid="booking-rates-retry" onClick={retryRates} className="min-h-11 w-full rounded-full border border-emerald-200 px-4 py-2 text-sm font-bold text-[#064E3B]">{t("access_retry")}</button>}
            {item.type === "tour" && <LevyNote city={item.city} testid="booking-levy-note" />}
            <button disabled={busy || !ratesReady} data-testid="booking-submit-btn" className="flex w-full items-center justify-center gap-2 rounded-full bg-[#E11D48] py-3.5 font-bold text-white transition-colors hover:bg-[#BE123C] disabled:opacity-60">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <MessageCircle className="h-5 w-5" />}{t("confirm_wa")}
            </button>
            </div>
          </form>
        )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
